import React, { Component } from 'react';
import { Currency } from '~/Api/Currency';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Heading from '~/UI/Heading/Heading';
import Popover from '~/UI/Popover';
import compactRadioStyles from '~/UI/Radio/compact.less';
import Radio from '~/UI/Radio/Radio';
import altSelectStyles from '~/UI/Select/alt.less';
import Select from '~/UI/Select/Select';

import styles from './styles.less';

export interface PriceType {
	code: string | null;
	label: string;
	condensedLabel: string | null;
}

interface Props {
	currencies: Currency[] | null;
	isFetchingCurrencies: boolean;
	priceTypes: PriceType[];
	currentPriceType: string | null;
	currentCurrency: string | null;
	filterCalculatedPrice: boolean;
	width: number;
	referenceRect: ClientRect;
	onFetch(): void;
	onChange(a: string | null, b: string | null, c?: boolean): void;
	onClose(event: MouseEvent): void;
}

export default class SelectPopover extends Component<Props> {
	currencyContainerElement: HTMLDivElement | null = null;

	currentCurrencyRadioElement: HTMLDivElement | null = null;

	componentDidMount() {
		const { currencies, onFetch } = this.props;
		if (currencies === null) {
			onFetch();
		}

		if (
			this.currentCurrencyRadioElement !== null &&
			this.currentCurrencyRadioElement.offsetTop + this.currentCurrencyRadioElement.offsetHeight >
				(this.currencyContainerElement?.offsetHeight || 0)
		) {
			if (this.currencyContainerElement) {
				this.currencyContainerElement.scrollTop = this.currentCurrencyRadioElement.offsetTop - 10;
			}
		}
	}

	render() {
		const {
			currencies,
			isFetchingCurrencies,
			priceTypes,
			currentPriceType,
			currentCurrency,
			filterCalculatedPrice,
			width,
			referenceRect,
			onClose,
			onChange,
		} = this.props;

		return (
			<Popover position='top' referenceRect={referenceRect} width={width} onClose={onClose}>
				<div className={styles.columnLeft} data-testid='price-radio-list'>
					<Heading level={3}>
						<strong>Price</strong>
					</Heading>
					<div className={styles.radio}>
						<Radio
							checked={currentPriceType === 'ex-factory' && !filterCalculatedPrice}
							id='price-type-ex-factory'
							name='priceType'
							styles={compactRadioStyles}
							onChange={() => onChange(currentCurrency, 'ex-factory', false)}
						>
							Ex-factory (calculated, if N/A in dataset)
						</Radio>
					</div>
					<div className={styles.radio}>
						<Radio
							checked={currentPriceType === null}
							id='price-type-first-available'
							name='priceType'
							styles={compactRadioStyles}
							onChange={() => onChange(currentCurrency, null, false)}
						>
							First available price in the distr. chain
						</Radio>
					</div>

					<p className={styles.line}>
						<span className={styles.lineTitle}>or</span>
					</p>

					<Select
						styles={altSelectStyles}
						value={
							(filterCalculatedPrice || (currentPriceType && currentPriceType !== 'ex-factory')
								? currentPriceType
								: '') || undefined
						}
						onChange={(event) => onChange(currentCurrency, event.target.value, event.target.value === 'ex-factory')}
					>
						<option disabled value=''>
							Select other price type
						</option>
						{priceTypes.map((priceType) => (
							<option
								disabled={priceType.code === 'lowest' && currentCurrency === null}
								key={`price-type-${priceType.code}`}
								value={priceType.code || ''}
							>
								{priceType.label}
							</option>
						))}
					</Select>
				</div>

				<div className={styles.columnRight}>
					<Heading level={3}>
						<strong>Currency</strong>
					</Heading>
					{currencies ? (
						<div
							className={styles.scrollContainer}
							data-testid='currency-radio-list'
							ref={(ref) => (this.currencyContainerElement = ref)}
						>
							<div
								className={styles.radio}
								ref={currentCurrency === null ? (ref) => (this.currentCurrencyRadioElement = ref) : null}
							>
								<Radio
									checked={currentCurrency === null}
									id='currency-source'
									name='currency'
									styles={compactRadioStyles}
									onChange={() => onChange(null, currentPriceType === 'lowest' ? null : currentPriceType)}
								>
									Source
								</Radio>
							</div>
							{currencies.map((currency) => (
								<div
									className={styles.radio}
									key={currency.code}
									ref={currency.code === currentCurrency ? (ref) => (this.currentCurrencyRadioElement = ref) : null}
								>
									<Radio
										checked={currency.code === currentCurrency}
										id={`currency-${currency.code}`}
										name='currency'
										styles={compactRadioStyles}
										onChange={() => onChange(currency.code, currentPriceType)}
									>
										{currency.code}
									</Radio>
								</div>
							))}
						</div>
					) : isFetchingCurrencies ? (
						<ActivityIndicator />
					) : null}
				</div>

				<p className={styles.note}>Source: European Central Bank, updated daily</p>
			</Popover>
		);
	}
}
